import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { MdArrowBack } from "@react-icons/all-files/md/MdArrowBack";
import { MdSearch } from "@react-icons/all-files/md/MdSearch";
import { MdClose } from "@react-icons/all-files/md/MdClose";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Loader from "@shared/loader";
import Transition from "@shared/transitionSlideUp";
import {
  IBankBranchDetails,
  ValidateIFSC,
  clearBankDetails,
  setError,
} from "../../../../../store/slices/ifscSlice";
import {
  KYCConstants,
  KYCRegxConstants,
} from "../../../../../constants/application";
import { DialogBody, Header, Item, ItemContainer } from "./search.styles";
import StickyActionContainer from "@shared/stickyActionContainer";

interface ISearch {
  title: string;
  close: () => void;
  open: boolean;
  ifscSelection?: (ifscDetails: IBankBranchDetails | null | string) => void;
}

// For IFSC code searching
function Search({ title, close, open, ifscSelection }: ISearch) {
  const { t } = useTranslation();
  const [text, setText] = useState<string>("");
  const [isIfscValid, setIsIfscValid] = useState<boolean>(false);
  const dispatch = useDispatch();
  const ifsc = useSelector((state: any) => state.ifsc);

  useEffect(() => {
    if (ifsc?.bankDetails) {
      setIsIfscValid(true);
      if (typeof ifsc?.bankDetails === "object") {
        setText(ifsc.bankDetails?.ifscCode);
      } else {
        setText(ifsc?.bankDetails);
      }
    }
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Once IFC code is met then only trigger the API call
    // don't remove if condition , as character max length is not working in inapp browser, this is work around
    if (e.target.value.length <= KYCConstants.ifscCodeCharLimit) {
      const _text = e.target.value.toUpperCase();
      setText(_text);
      if (KYCRegxConstants.isfcRegx.test(_text)) {
        setIsIfscValid(true);
        dispatch(ValidateIFSC({ IFSC: _text }) as any);
      } else {
        setIsIfscValid(false);
        if (ifsc.bankDetails) {
          dispatch(clearBankDetails() as any);
        }
      }
    }
  };

  const processClickAction = () => {
    if (typeof ifsc?.bankDetails === "object") {
      ifscSelection?.(ifsc.bankDetails as IBankBranchDetails);
    } else {
      ifscSelection?.(ifsc.bankDetails as string);
    }
  };

  return (
    <Dialog fullScreen open={open} TransitionComponent={Transition}>
      <Header>
        <IconButton
          id={"backButton"}
          onClick={() => (isIfscValid ? close() : ifscSelection)?.(null)}
        >
          <MdArrowBack />
        </IconButton>
        <Typography variant="h6">{title}</Typography>
      </Header>
      <DialogBody>
        <TextField
          fullWidth
          autoFocus
          color="success"
          value={text}
          onChange={handleChange}
          slotProps={{
            htmlInput: { maxLength: KYCConstants.ifscCodeCharLimit },
            input: {
              name: "ifscSearch",
              autoComplete: "off",
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch size={25} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  onClick={() => {
                    setText("");
                    setIsIfscValid(false);
                    dispatch(clearBankDetails() as any);
                  }}
                  component={IconButton}
                  position="end"
                >
                  <MdClose size={25} />
                </InputAdornment>
              ),
            },
          }}
          variant="outlined"
        />
        <section>
          <Typography color="#0000008A;">
            {isIfscValid && ifsc.success
              ? t("one_result_found", { defaultValue: "1 result found." })
              : t("ifsc_hint_message", {
                  defaultValue: "Enter IFSC code.Example: ABCD0000123",
                })}
          </Typography>
          {ifsc.bankDetails && typeof ifsc?.bankDetails === "object" && (
            <ItemContainer>
              <Item
                id="ifsc-result"
                onClick={() =>
                  ifscSelection?.(ifsc.bankDetails as IBankBranchDetails)
                }
              >
                <Typography fontWeight={"bold"} color="#333333">
                  {ifsc?.bankDetails?.ifscCode}
                  <div>
                    <small>({ifsc?.bankDetails?.bank})</small>
                  </div>
                </Typography>
                <Typography color="#333333" sx={{ textWrap: "auto" }}>
                  {ifsc?.bankDetails?.address}
                </Typography>
              </Item>
            </ItemContainer>
          )}
        </section>
      </DialogBody>
      <StickyActionContainer>
        <Button
          onClick={processClickAction}
          disabled={!isIfscValid || ifsc?.loading}
          variant="contained"
          size="large"
        >
          {t("proceed_text", { defaultText: "Proceed" })}
        </Button>
      </StickyActionContainer>
      {ifsc.loading && <Loader />}
      <Snackbar
        open={ifsc?.error}
        onClose={() => dispatch(setError({ value: "" }) as any)}
        autoHideDuration={KYCConstants.autoHideDuration}
        message={ifsc?.error}
        sx={{ bottom: { xs: 90, sm: 0 } }}
      />
    </Dialog>
  );
}

export default Search;
